import { useState, useEffect } from 'react';
import { useForm } from '@mantine/form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Table,
  Text,
  Modal,
  Stack,
  TextInput,
  NumberInput,
  Group,
  ActionIcon,
  Select,
  Flex,
} from '@mantine/core';
import {
  IconPencil,
  IconCircleCheck,
  IconTrash,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { Page, ProcessHeader, YesNoToggle } from '@/components';
import { getRouteById, routeWithParams } from '@/helpers/routes';
import {
  useCalculateCycleMortgageWithSamePeriodMutation,
  useCalculateCycleMortgageWithLongerPeriodMutation,
  useCalculateCycleMortgageWithShorterPeriodMutation,
  useSaveMutation,
  useCompareloanFromPdfFileMutation,
  useGetTranchesDataMutation,
} from '@/services/api';
import { getMutationResult } from '@/helpers/query';
import routes from '@/routes';
import { updateMortgageCycle } from '@/data/features/mortgageCycle/mortgageCycle';
import { formatNumberWithCommas } from '@/helpers/string';
import { roundNumber } from '@/helpers/number';
import { percentage } from '@/helpers/validations';
import { MORTGAGE_CYCLE_ACTIONS } from '@/data/constants';
import RadioButton from '@/components/RadioButton';
import ProcessButton from '@/components/ProcessButton';
import { toISOString } from '@/helpers/date';
import { updateExtraDetails } from '@/data/features/proposal/proposal';
import documentGrayIcon from '@/assets/images/documentGray.svg?react'; // eslint-disable-line import/no-unresolved
import ReducingRepaymentWithoutChangingThePeriod from '@/assets/images/reducingRepaymentWithoutChangingThePeriod.svg?react'; // eslint-disable-line import/no-unresolved
import ReducingRepaymentAndIncreasingTheLoanPeriod from '@/assets/images/reducingRepaymentAndIncreasingTheLoanPeriod.svg?react'; // eslint-disable-line import/no-unresolved
import IncreasingTheRepaymentAndReducingThePeriod from '@/assets/images/increasingTheRepaymentAndReducingThePeriod.svg?react'; // eslint-disable-line import/no-unresolved
import classes from './loansComparation.module.css';

const LoansComparation = () => {
  const { t } = useTranslation();
  const [calcLoading, setCalcLoading] = useState(false);
  const mortgageCycleDetails = useSelector((state) => state.mortgageCycle);
  const { comparisonData, bank, mortgageCycleAction } = mortgageCycleDetails;
  // Edit loan modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [data, setData] = useState(comparisonData || []);
  const [apiSave] = useSaveMutation();
  const [apiCompare] = useCompareloanFromPdfFileMutation();
  const [apiGetTranches] = useGetTranchesDataMutation();
  const [editingLoan, setEditingLoan] = useState(null);
  const navigate = useNavigate();
  const proposal = useSelector((state) => state.proposal);
  const [saveEditLoading, setSaveEditLoading] = useState(false);
  const [tranchesData, setTranchesData] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loanToDelete, setLoanToDelete] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const trancheOptions = tranchesData.map((tranche) => ({
    value: tranche.hebrew,
    label: tranche.hebrew,
  }));

  const dispatch = useDispatch();
  const { id, extraDetails } = proposal;
  const {
    oldestBorrowerDOB, youngestBorrowerDOB, isRecycling, cycleMortgage, loanTypeName,
  } = extraDetails;
  const isRecyclingLoan = isRecycling === 'yes';

  const [calculateCycleMortgageWithSamePeriod] = useCalculateCycleMortgageWithSamePeriodMutation();
  const [
    calculateCycleMortgageWithLongerPeriod,
  ] = useCalculateCycleMortgageWithLongerPeriodMutation();
  const [
    calculateCycleMortgageWithShorterPeriod,
  ] = useCalculateCycleMortgageWithShorterPeriodMutation();

  const form = useForm({
    mode: 'controlled',
    initialValues: {
      bank: bank || '',
      mortgageCycleAction: mortgageCycleAction || '',
      isMortgageRepaymentIncludedInPreviousLiabilities: mortgageCycleDetails?.isMortgageRepaymentIncludedInPreviousLiabilities || '',
    },
  });

  const { values } = form;

  // Initialize data with currentLoans from store if available
  useEffect(() => {
    if (mortgageCycleDetails?.currentLoans?.length > 0 && data.length === 0) {
      setData(mortgageCycleDetails.currentLoans);
    }
  }, [mortgageCycleDetails?.currentLoans, data.length]);

  // Check if any loan (improvable or not) has missing data

  const hasMissingData = data.some((loan) => {
    const requiredFields = [
      loan.loanNumber,
      loan.loanPurpose,
      loan.loanAmount,
      loan.monthsLeft,
      loan.interestType,
      loan.interest,
      loan.totalAmount,
    ];

    // Check if any required field is missing (null, undefined, or empty string)
    const hasMissingRequiredField = requiredFields.some((field) => !field);

    // Check if monthlyPayment is missing (null or undefined, but allow 0)
    const hasMissingMonthlyPayment = loan.monthlyPayment === null
      || loan.monthlyPayment === undefined;

    return hasMissingRequiredField || hasMissingMonthlyPayment;
  });

  // Interest that exists but is malformed: a comma instead of a decimal point
  // (e.g. "4,450000" from the bank file), a value above 100, or anything
  // non-numeric. A missing interest is already covered by `hasMissingData`.
  const isInvalidInterest = (loan) => loan?.interest !== null
    && loan?.interest !== undefined
    && loan?.interest !== ''
    && !percentage(loan.interest);

  const invalidInterestLoans = data.filter(isInvalidInterest);

  const handleReturn = () => {
    navigate(routeWithParams(getRouteById(routes.newProposal.id, 'uploadBankFile')?.path, { id }));
  };

  // Edit loan functions
  const handleEditLoan = async (loan, idx) => {
    try {
      const result = await getMutationResult({}, apiGetTranches);
      setTranchesData(result.data.tranches);
    } catch (error) {
      notifications.show({
        title: t('globals.error.title'),
        message: t('globals.error.message'),
      });
    }

    setEditingIndex(idx);
    // A malformed interest (e.g. "4,450000") cannot be rendered faithfully by
    // NumberInput, so clear it and let the user retype it rather than show a
    // value that differs from what is stored.
    setEditingLoan(isInvalidInterest(loan) ? { ...loan, interest: '' } : { ...loan });
    setIsEditModalOpen(true);
  };

  const compareData = (loans = []) => loans.map((loan) => ({
    ...loan,
    originalMonthsLeft: loan.monthsLeft,
  }));

  const handleSaveEdit = async () => {
    setSaveEditLoading(true);

    if (editingLoan == null || editingIndex == null) {
      setSaveEditLoading(false);
      return;
    }

    const isMissingInterest = editingLoan.interest === null
      || editingLoan.interest === undefined
      || editingLoan.interest === '';

    if (isMissingInterest || isInvalidInterest(editingLoan)) {
      notifications.show({
        title: t('globals.error.title'),
        message: isMissingInterest
          ? t('pages.loansComparation.validations.missingInterest')
          : t('globals.validations.validPercent'),
      });
      setSaveEditLoading(false);
      return;
    }

    const updatedLoans = [...data];
    updatedLoans[editingIndex] = editingLoan;

    const compareResult = await getMutationResult({ loans: updatedLoans }, apiCompare);

    const comparedData = compareData(compareResult.data.loans);
    setData(compareResult.data.loans);

    // Update the store
    dispatch(updateMortgageCycle({
      ...mortgageCycleDetails,
      currentLoans: updatedLoans,
      comparisonData: comparedData,
    }));

    dispatch(updateExtraDetails({
      cycleMortgage:
        { ...cycleMortgage, currentLoans: updatedLoans },
    }));

    await apiSave({ id, cycleMortgage: { loans: comparedData } });

    setIsEditModalOpen(false);
    setEditingLoan(null);
    setEditingIndex(null);
    setSaveEditLoading(false);
  };

  const handleDeleteLoan = async (idx) => {
    try {
      const updatedLoans = data.filter((_, i) => i !== idx);
      let comparedData = [];

      if (updatedLoans.length > 0) {
        const compareResult = await getMutationResult({ loans: updatedLoans }, apiCompare);
        const comparedLoans = compareResult.data.loans;
        comparedData = compareData(comparedLoans);
        setData(comparedLoans);
      } else {
        setData([]);
      }

      dispatch(updateMortgageCycle({
        ...mortgageCycleDetails,
        currentLoans: updatedLoans,
        comparisonData: comparedData,
      }));

      dispatch(updateExtraDetails({
        cycleMortgage: {
          ...cycleMortgage, currentLoans: updatedLoans,
        },
      }));

      await apiSave({ id, cycleMortgage: { loans: comparedData } });
    } catch (error) {
      notifications.show({
        title: t('globals.error.title'),
        message: t('globals.error.message'),
      });
    }
  };

  const confirmDeleteLoan = async () => {
    if (deleteIndex == null) return;

    try {
      setDeleteLoading(true);
      await handleDeleteLoan(deleteIndex);
      closeDeleteModal();
    } finally {
      setDeleteLoading(false);
    }
  };

  const openDeleteModal = (idx) => {
    setDeleteIndex(idx);
    setLoanToDelete(data[idx]);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setLoanToDelete(null);
    setDeleteIndex(null);
  };

  const mortgageCycleActionsOptionsArray = [
    {
      value: MORTGAGE_CYCLE_ACTIONS.reductionRepaymentWithoutChangingTheTermByConsolidatingLoans,
      icon: <ReducingRepaymentWithoutChangingThePeriod />,
      label: t('pages.mortgageCycle.mortgageCycleAction.options.reductionRepaymentWithoutChangingTheTermByConsolidatingLoans'),
    },
    {
      // eslint-disable-next-line max-len
      value: MORTGAGE_CYCLE_ACTIONS.reductionOfRepaymentAndExtensionOfTheLoanTermThroughLoanConsolidation,
      icon: <ReducingRepaymentAndIncreasingTheLoanPeriod/>,
      label: t('pages.mortgageCycle.mortgageCycleAction.options.reductionOfRepaymentAndExtensionOfTheLoanTermThroughLoanConsolidation'),
    },
    {
      value: MORTGAGE_CYCLE_ACTIONS.reducingTheLoanPeriod,
      icon: <IncreasingTheRepaymentAndReducingThePeriod />,
      label: t('pages.mortgageCycle.bankFileUpload.mortgageCycleAction.options.increasingTheRepaymentAndReducingThePeriod'),
    },
  ];

  const handleSubmit = async () => {
    try {
      setCalcLoading(true);
      let response;
      let mortageCycleForAllLoanInCycleWithAdditionalLoan;
      let additionalOutcome = 0;

      const {
        mortgageCycleAction: formMortgageCycleAction,
        isMortgageRepaymentIncludedInPreviousLiabilities,
      } = values;

      if (isRecyclingLoan && isMortgageRepaymentIncludedInPreviousLiabilities === 'no') {
        additionalOutcome = mortgageCycleDetails.currentLoans.reduce(
          (sum, loan) => sum + loan.monthlyPayment,
          0,
        );
      }

      let loans = data;

      // In case of loan recycling, we cycle only loans where `isBetterToChange === false`
      if (isRecyclingLoan) {
        values.mortgageCycleAction = '';
        loans = data.filter((loan) => !loan.isBetterToChange);

        if (loanTypeName === 'anyPurposeWithPropertyCollateral') {
          loans = data.filter((loan) => !loan.isBetterToChange || loan.loanPurpose === 'דיור');
        }

        const calculateData = {
          loans,
          isMergingLoans: true,
        };
        if (loans.length === 0) {
          // If there are no loans to cycle, we create a default response with zero values
          response = {
            data: {
              totalsBeforeCalculation: {
                totalAmount: 0,
                newMonthlyPayment: 0,
                interestRepayment: 0,
                linkageRepayment: 0,
                principalRepayment: 0,
                finalPayment: 0,
                finalInterestRepayment: 0,
                finalLinkageRepayment: 0,
                maxMonthlyPayment: 0,
                returnConversion: 0,
                monthsLeft: 0,
              },
              totalsAfterCalculation: {
                totalAmount: 0,
                newMonthlyPayment: 0,
                interestRepayment: 0,
                linkageRepayment: 0,
                principalRepayment: 0,
                finalPayment: 0,
                finalInterestRepayment: 0,
                finalLinkageRepayment: 0,
                maxMonthlyPayment: 0,
                returnConversion: 0,
                monthsLeft: 0,
                totalSaving: 0,
                monthlySaving: 0,
              },
            },
          };
        } else {
          response = await calculateCycleMortgageWithSamePeriod(calculateData).unwrap();
        }

        const mortageCycleForAllLoanInCycleWithAdditionalLoanObj = {
          loans: data,
          isMergingLoans: true,
        };
        // eslint-disable-next-line max-len
        mortageCycleForAllLoanInCycleWithAdditionalLoan = await calculateCycleMortgageWithSamePeriod(mortageCycleForAllLoanInCycleWithAdditionalLoanObj).unwrap();
      } else {
        switch (formMortgageCycleAction) {
          case MORTGAGE_CYCLE_ACTIONS.reducingTheLoanPeriod:
            response = await calculateCycleMortgageWithShorterPeriod(loans).unwrap();
            break;

          // eslint-disable-next-line max-len
          case MORTGAGE_CYCLE_ACTIONS.reductionRepaymentWithoutChangingTheTermByConsolidatingLoans: {
            const calculateData = {
              loans,
              isMergingLoans: true,
            };

            response = await calculateCycleMortgageWithSamePeriod(calculateData).unwrap();
            break;
          }

          // eslint-disable-next-line max-len
          case MORTGAGE_CYCLE_ACTIONS.reductionOfRepaymentAndExtensionOfTheLoanTermThroughLoanConsolidation: {
            const formattedOldestBorrowerDOB = toISOString(oldestBorrowerDOB);
            const formattedYoungestBorrowerDOB = toISOString(youngestBorrowerDOB);

            const calculatedData = {
              loans,
              oldestBorrowerDOB: formattedOldestBorrowerDOB,
              youngestBorrowerDOB: formattedYoungestBorrowerDOB,
              isMergingLoans: true,
            };

            response = await calculateCycleMortgageWithLongerPeriod(calculatedData).unwrap();
            break;
          }

          default:
            break;
        }
      }

      await apiSave({
        id,
        additionalOutcome,
        cycleMortgage: {
          mortgageCycleAction: formMortgageCycleAction,
          isMortgageRepaymentIncludedInPreviousLiabilities,
        },
      }).unwrap();

      // ...inside the component
      dispatch(
        updateMortgageCycle({
          calculationData: response.data,
          currentLoans: data,
          mortgageCycleAction: values.mortgageCycleAction,
          bank: values.bank,
          isMortgageRepaymentIncludedInPreviousLiabilities,
          additionalOutcome,
          // eslint-disable-next-line max-len
          mortageCycleForAllLoanInCycleWithAdditionalLoan: mortageCycleForAllLoanInCycleWithAdditionalLoan?.data,
        }),
      );

      dispatch(updateExtraDetails({
        cycleMortgage: {
          ...cycleMortgage,
          mortgageCycleAction: values.mortgageCycleAction,
          isMortgageRepaymentIncludedInPreviousLiabilities,
        },
        additionalOutcome,
      }));

      const nextPage = !hasLoansToImprove ? 'loanAmount' : 'mortgageCycleAction';
      navigate(routeWithParams(getRouteById(routes.newProposal.id, nextPage).path, { id }));
    } catch (error) {
      notifications.show({
        title: t('globals.error.title'),
        message: t('globals.error.message'),
      });
    } finally {
      setCalcLoading(false);
    }
  };

  const handleContinue = () => {
    if (hasMissingData) {
      notifications.show({
        title: t('globals.error.title'),
        message: t('חסרים נתונים בהלוואות. אנא בדוק את הנתונים ונסה שוב.'),
      });
      return;
    }
    if (invalidInterestLoans.length > 0) {
      const details = invalidInterestLoans
        .map((loan) => `${loan.loanNumber} (${loan.interest})`)
        .join(', ');

      notifications.show({
        title: t('globals.error.title'),
        message: t('pages.loansComparation.validations.invalidInterest', { loans: details }),
      });
      return;
    }
    if (isRecyclingLoan && !values.isMortgageRepaymentIncludedInPreviousLiabilities) {
      notifications.show({
        title: t('globals.error.title'),
        message: t('אנא בחר האם סכום החזר המשכנתא נכלל בהתחייבויות'),
      });
      return;
    }
    if (!isRecyclingLoan && !values.mortgageCycleAction) {
      notifications.show({
        title: t('globals.error.title'),
        message: t('אנא בחר פעולת מחזור משכנתא'),
      });
      return;
    }
    handleSubmit();
  };

  const hasLoansToImprove = data.filter((loan) => loan.isBetterToChange).length > 0;
  const showRadioButtons = hasLoansToImprove && !hasMissingData && !isRecyclingLoan;
  const missingLiabilitySelection = (
    !values.mortgageCycleAction
    && !values.isMortgageRepaymentIncludedInPreviousLiabilities
  );

  const canContinue = data.length < 1
    || missingLiabilitySelection
    || hasMissingData
    || invalidInterestLoans.length > 0;

  return (
    <ProcessHeader
      headerText={t('pages.loansComparation.title')}
      headerColor="#DCE0E5"
      headerTextColor="#525E6F"
      showReturnButton
      onReturn={handleReturn}
      icon={documentGrayIcon}
    >

      {/* Current Loans Table */}
      {data && data.length > 0 && (
        <Page.Section
          withCollapse
          onCollapseChange={() => { }}
          title={t('pages.loansComparation.title')}
        >
          <div className={classes.tableContainer}>
            <Table withColumnBorders className={classes.table}>
              <Table.Thead>
                <Table.Tr className={classes.tableHeader}>
                  <Table.Th className={classes.tableHeaderCell}>מספר הלוואה</Table.Th>
                  <Table.Th className={classes.tableHeaderCell}>מטרת הלוואה</Table.Th>
                  <Table.Th className={classes.tableHeaderCell}>סכום הלוואה מקורי</Table.Th>
                  <Table.Th className={classes.tableHeaderCell}>סוג ריבית</Table.Th>
                  <Table.Th className={classes.tableHeaderCell}>ריבית (%)</Table.Th>
                  <Table.Th className={classes.tableHeaderCell}>תקופה שנותרה לסילוק</Table.Th>
                  <Table.Th className={classes.tableHeaderCell}>החזר חודשי נוכחי</Table.Th>
                  <Table.Th className={classes.tableHeaderCell}>סכום שנותר לסילוק</Table.Th>
                  <Table.Th className={classes.tableHeaderCell}>עריכה</Table.Th>
                  <Table.Th className={classes.tableHeaderCell}>מחיקה</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.map((loan, idx) => (
                  <Table.Tr key={idx}>
                    <Table.Td className={classes.tableDataCell}>{loan.loanNumber || '-'}</Table.Td>
                    <Table.Td className={classes.tableDataCell}>{loan.loanPurpose || '-'}</Table.Td>
                    <Table.Td className={classes.tableDataCell}>{loan.loanAmount !== null && loan.loanAmount !== undefined ? `₪ ${formatNumberWithCommas(roundNumber(loan.loanAmount))}` : '-'}</Table.Td>
                    <Table.Td className={classes.tableDataCell}>{loan.interestType || '-'}</Table.Td>
                    <Table.Td className={isInvalidInterest(loan) ? `${classes.tableDataCell} ${classes.invalidDataCell}` : classes.tableDataCell}>{loan.interest !== null && loan.interest !== undefined ? loan.interest : '-'}</Table.Td>
                    <Table.Td className={classes.tableDataCell}>{loan.monthsLeft ? `${loan.monthsLeft} חודשים` : '-'}</Table.Td>
                    <Table.Td className={classes.tableDataCell}>{loan.monthlyPayment !== null && loan.monthlyPayment !== undefined ? `₪ ${formatNumberWithCommas(roundNumber(loan.monthlyPayment))}` : '-'}</Table.Td>
                    <Table.Td className={classes.tableDataCell}>{loan.totalAmount !== null && loan.totalAmount !== undefined ? `₪ ${formatNumberWithCommas(roundNumber(loan.totalAmount))}` : '-'}</Table.Td>
                    <Table.Td className={classes.tableDataCell}>
                      <ActionIcon
                        variant="subtle"
                        color="blue"
                        size="sm"
                        onClick={() => handleEditLoan(loan, idx)}
                      >
                        <IconPencil size={16} />
                      </ActionIcon>
                    </Table.Td>
                    <Table.Td className={classes.tableDataCell}>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        onClick={() => openDeleteModal(idx)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}

                {/* Subtotal Row */}
                <Table.Tr>
                  <Table.Td colSpan={5} className={classes.emptyCell}></Table.Td>
                  <Table.Td className={classes.subtotalCell}>
                    סה"כ
                  </Table.Td>
                  <Table.Td className={classes.subtotalCell}>
                    ₪ {formatNumberWithCommas(roundNumber(
                    data.reduce((sum, loan) => sum + (loan.monthlyPayment || 0), 0),
                  ))}
                  </Table.Td>
                  <Table.Td className={classes.subtotalCell}>
                    ₪ {formatNumberWithCommas(roundNumber(
                    data.reduce((sum, loan) => sum + (loan.totalAmount || 0), 0),
                  ))}
                  </Table.Td>
                  <Table.Td className={classes.emptyCell}></Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </div>
        </Page.Section>
      )}

      {data.filter((loan) => loan.isBetterToChange).length > 0 && hasMissingData && (
        <Page.Section withBackground>
          <Text ta="center" size="md" fw={500} c="red" mb="md">
            {t('חסרים נתונים בהלוואות. אנא בדוק את הנתונים ונסה שוב.')}
          </Text>
        </Page.Section>
      )}
      {data.filter((loan) => loan.isBetterToChange).length === 0 && !isRecyclingLoan
        && (
        <Group align="center">
          <IconCircleCheck color="var(--mantine-color-green-6)" />
          <Text c="var(--mantine-color-green-6)">
            {t('pages.mortgageCycle.bankFileUpload.notBetterToChange.title')}
          </Text>
        </Group>
        )
      }
      { showRadioButtons && (
        <Page.Section
          title={t('pages.mortgageCycle.bankFileUpload.mortgageCycleAction.label')}
        >
          <RadioButton
            options={mortgageCycleActionsOptionsArray}
            fieldName="mortgageCycleAction"
            form={form}
            value={values.mortgageCycleAction}
            onChange={(value) => {
              form.setFieldValue('mortgageCycleAction', value);
            }}
          />
        </Page.Section>
      )}

      {
        isRecyclingLoan && (
          <Page.Section>
            <YesNoToggle
              name='isMortgageRepaymentIncludedInPreviousLiabilities'
              label={t('pages.mortgageCycle.bankFileUpload.isMortgageRepaymentIncludedInPreviousLiabilities')}
              value={values.isMortgageRepaymentIncludedInPreviousLiabilities}
              onChange={(value) => form.setFieldValue('isMortgageRepaymentIncludedInPreviousLiabilities', value)}
              required
              error={form.errors.isMortgageRepaymentIncludedInPreviousLiabilities}
              yesColor='#E91E63'
              noColor='#4CAF50'
              boldLabel ={true}
            />
          </Page.Section>

        )
      }
      {/* Edit Loan Modal */}
      <Modal
        opened={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingLoan(null);
          setEditingIndex(null);
        }}
        title={t('ערוך הלוואה')}
        size="lg"
      >
        <Stack>
          <TextInput
            label={t('מספר הלוואה')}
            value={editingLoan?.loanNumber || ''}
            onChange={(e) => setEditingLoan({ ...editingLoan, loanNumber: e.target.value })}
          />
          <TextInput
            label={t('מטרת הלוואה')}
            value={editingLoan?.loanPurpose || ''}
            onChange={(e) => setEditingLoan({ ...editingLoan, loanPurpose: e.target.value })}
          />
          <NumberInput
            label={t('סכום הלוואה')}
            value={editingLoan?.loanAmount || 0}
            onChange={(value) => setEditingLoan({ ...editingLoan, loanAmount: value })}
            min={0}
          />
          <TextInput
            label={t('תאריך התחלה')}
            value={editingLoan?.startDate || ''}
            onChange={(e) => setEditingLoan({ ...editingLoan, startDate: e.target.value })}
          />
          <TextInput
            label={t('תאריך סיום')}
            value={editingLoan?.endDate || ''}
            onChange={(e) => setEditingLoan({ ...editingLoan, endDate: e.target.value })}
          />
          <NumberInput
            label={t('חודשים שנשארו')}
            value={editingLoan?.monthsLeft || 0}
            onChange={(value) => setEditingLoan({ ...editingLoan, monthsLeft: value })}
            min={0}
          />
          <NumberInput
            label={t('ריבית (%)')}
            value={editingLoan?.interest ?? ''}
            onChange={(value) => setEditingLoan({ ...editingLoan, interest: value })}
            min={0}
            max={100}
            error={isInvalidInterest(editingLoan)
              ? t('globals.validations.validPercent')
              : null}
          />
          <Select
            label={t('סוג הריבית')}
            data={trancheOptions}
            value={editingLoan?.interestType || ''}
            onChange={(value) => setEditingLoan((prev) => ({
              ...prev,
              interestType: value,
              interestTypeToCompare: value,
            }))}
            placeholder={t('בחר סוג ריבית')}
          />
          <NumberInput
            label={t('תשלום חודשי')}
            value={editingLoan?.monthlyPayment || 0}
            onChange={(value) => setEditingLoan({ ...editingLoan, monthlyPayment: value })}
            min={0}
          />
          <NumberInput
            label={t('הסכום שנשאר לתשלום')}
            value={editingLoan?.totalAmount || 0}
            onChange={(value) => setEditingLoan({ ...editingLoan, totalAmount: value })}
            min={0}
          />
          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={() => {
                setIsEditModalOpen(false);
                setEditingLoan(null);
              }}
            >
              {t('globals.cancel')}
            </Button>
            <Button onClick={handleSaveEdit} loading={saveEditLoading}>
              {t('globals.save')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={isDeleteModalOpen}
        onClose={closeDeleteModal}
        title={t('pages.LoansComparation.deleteModal.title')}
        centered
      >
        <Stack gap="md">
          <Text fw={600}>
            {t('pages.LoansComparation.deleteModal.subtitle', { loanNumber: loanToDelete?.loanNumber })}
          </Text>

          <Text c="dimmed" size="sm">
            {t('pages.LoansComparation.deleteModal.text.title')}
            <br />
            {t('pages.LoansComparation.deleteModal.text')}
          </Text>

          <Flex direction='row' justify='center'>
            <Group>
              <Button
                variant="outline"
                onClick={closeDeleteModal}
                disabled={deleteLoading}
              >
                {t('pages.LoansComparation.deleteModal.action.cancel')}
              </Button>
              <Button
                color="red"
                onClick={confirmDeleteLoan}
                loading={deleteLoading}
                disabled={deleteLoading}
              >
                {t('pages.LoansComparation.deleteModal.action.ok')}
              </Button>
            </Group></Flex>
        </Stack>
      </Modal>

      {(
        data.filter((loan) => loan.isBetterToChange).length > 0
        || isRecyclingLoan
      ) && (
        <Page.Actions>
          <ProcessButton
          onClick={handleContinue}
          disabled={canContinue}
          loading={calcLoading}
        />
        </Page.Actions>)}
    </ProcessHeader>
  );
};

export default LoansComparation;
